import { STEP_UP_OAUTH_CONFIG } from './constants';
import { PKCE_CODE_CHALLENGE_METHOD } from './pkce';
import type {
  StepUpOAuthConfig,
  StepUpRequiredDetails,
  WebMessageResponse,
} from './types';

export type AuthorizeUrlConfig = Pick<
  StepUpOAuthConfig,
  'authorizeUrl' | 'redirectUri' | 'clientId' | 'scope'
>;

// Builds oauth2ng's `/authorize` URL for the web_message flow. `prompt: 'none'`
// requests a silent attempt; omit it for the interactive one.
export const buildAuthorizeUrl = (
  config: AuthorizeUrlConfig,
  args: {
    state: string;
    details: StepUpRequiredDetails;
    codeChallenge: string;
    prompt?: string;
  },
): string => {
  const { state, details, codeChallenge, prompt } = args;
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: STEP_UP_OAUTH_CONFIG.RESPONSE_TYPE,
    response_mode: STEP_UP_OAUTH_CONFIG.RESPONSE_MODE,
    // Must match the redirect_uri sent to /token.
    redirect_uri: config.redirectUri || window.location.origin,
    scope: config.scope,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: PKCE_CODE_CHALLENGE_METHOD,
  });
  // acr_values is a space-separated OR-list; omitted for a max_age-only step-up.
  if (details.acrValues.length) {
    params.set('acr_values', details.acrValues.join(' '));
  }
  if (details.maxAge != null) {
    params.set('max_age', String(details.maxAge));
  }
  if (prompt) {
    params.set('prompt', prompt);
  }
  return `${config.authorizeUrl}?${params.toString()}`;
};

// oauth2ng's web_message page is served from the authorize endpoint's origin, so
// that is the origin a transport must require. '' when authorizeUrl is unparseable.
export const authorizeEndpointOrigin = (authorizeUrl: string): string => {
  try {
    return new URL(authorizeUrl).origin;
  } catch {
    return '';
  }
};

export type WebMessageOutcome =
  | { kind: 'code'; code: string; state: string }
  | { kind: 'error'; error: string }
  | null;

// Accepts a web_message only from the authorize origin and with a matching `state`
// (CSRF), then classifies it. oauth2ng posts the same { code, state, error } shape to
// a popup's opener and to an iframe's parent.
export const readWebMessage = (
  event: MessageEvent,
  expectedOrigin: string,
  expectedState: string | null,
): WebMessageOutcome => {
  if (!expectedOrigin || event.origin !== expectedOrigin) {
    return null;
  }
  const data = event.data as Partial<WebMessageResponse> | undefined;
  if (!data || typeof data !== 'object') {
    return null;
  }
  if (typeof data.state !== 'string' || data.state !== expectedState) {
    return null;
  }
  if (data.error) {
    return { kind: 'error', error: data.error };
  }
  if (typeof data.code === 'string' && data.code) {
    return { kind: 'code', code: data.code, state: data.state };
  }
  return null;
};
